/**
 * ▶️ Resume Corporate Account Modal
 * Modal for resuming/reactivating a suspended corporate account
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  IconButton,
} from '@mui/material';
import { Close, PlayArrow } from '@mui/icons-material';
import {
  corporateAccountsService,
  CorporateAccount,
} from '@/services/corporateAccountsService';
import toast from 'react-hot-toast';

interface ResumeCorporateAccountModalProps {
  open: boolean;
  account: CorporateAccount | null;
  onClose: () => void;
  onResume: () => void;
}

export const ResumeCorporateAccountModal: React.FC<ResumeCorporateAccountModalProps> = ({
  open,
  account,
  onClose,
  onResume,
}) => {
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    setError('');

    if (!account) return;

    setSubmitting(true);
    try {
      const response = await corporateAccountsService.resumeCorporateAccount({
        accountId: account.id,
        notes: notes || undefined,
      });

      if (response.success) {
        toast.success(`${account.companyName} has been reactivated`);
        onResume();
        handleClose();
      } else {
        setError(response.error || 'Failed to resume account');
      }
    } catch (error) {
      console.error('Failed to resume account:', error);
      setError('Failed to resume account');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setNotes('');
    setError('');
    onClose();
  };

  if (!account) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Resume Corporate Account</Typography>
          <IconButton onClick={handleClose} disabled={submitting}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Alert severity="success" icon={<PlayArrow />} sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Reactivate Account:</strong> This account will be able to make new bookings again.
          </Typography>
        </Alert>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Account Details
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Company: <strong>{account.companyName}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Current Status: <strong>{account.status.toUpperCase()}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Credit Limit: <strong>{account.currency === 'NGN' ? '₦' : '$'}{account.creditLimit.toLocaleString()}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Available Credit: <strong>{account.currency === 'NGN' ? '₦' : '$'}{account.availableCredit.toLocaleString()}</strong>
          </Typography>
        </Box>

        <TextField
          fullWidth
          multiline
          rows={3}
          label="Notes (Optional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          disabled={submitting}
          helperText="Optional: Add notes about why this account is being reactivated"
          placeholder="e.g., Payment received, Issue resolved, Account reviewed..."
        />

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="success"
          onClick={handleSubmit}
          disabled={submitting}
          startIcon={<PlayArrow />}
        >
          {submitting ? 'Resuming...' : 'Resume Account'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
